import React from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { Box, Button, Grid, Typography } from '@mui/material';

import { ApplicationState } from 'store/store';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { AwardTemplate } from 'components/customers/models/Award';
import { LoyaltyScope } from 'components/loyalty/enums';
import { AwardRedemptionFormData } from 'components/perks/award-enrichments/forms/AwardRedemptionForm';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { PerkBasketQualificationSection } from './PerkBasketQualificationSection';
import { PerkDateTimeRestrictionSection } from './PerkDateTimeRestrictionSection';
import { PerkDiscountSection } from './PerkDiscountSection';
import { AwardRedemptionFullFormData } from './PerkRedemptionForm';

const basketScopeOptions: Option[] = [
    {
        value: 'ITEM',
        label: 'a specific product'
    },
    {
        value: 'CATEGORY',
        label: 'a specific category'
    },
    {
        value: 'ITEM_OR_CATEGORY',
        label: 'either a product or category'
    },
    {
        value: 'BASKET',
        label: 'nothing specific'
    }
];

interface RedemptionFormCardContentProps {
    onUpdate?: (data: AwardRedemptionFullFormData) => void;
    onPrevious?: (isValid?: boolean, values?: AwardRedemptionFormData) => void;
    clickPrevious: () => void;
    isEdit?: boolean;
    isLoading?: boolean;
    currencySymbol: string;
    perkType: string;
    toggledFields?: string[];
    onToggleChange?: (value: string[] | ((state: string[]) => string[])) => void;
    auto?: boolean;
}

export const RedemptionFormCardContent: React.FC<RedemptionFormCardContentProps> = ({
    onUpdate,
    onPrevious,
    isEdit,
    clickPrevious,
    isLoading,
    currencySymbol,
    perkType,
    toggledFields,
    onToggleChange,
    auto
}) => {
    const { submitForm, values, isValid } = useFormikContext<AwardRedemptionFullFormData>();
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const posProvider = React.useMemo(() => settings?.posProvider, [settings]);
    const merchantTimezone = React.useMemo(() => settings?.region?.timezone || 'Europe/London', [settings]);
    const isPseudoCurrency = React.useMemo(() => perkType === AwardTemplate.PSEUDO_CURRENCY, [perkType]);

    const handleUpdate = React.useCallback(() => {
        onUpdate(values);
    }, [onUpdate, values]);

    const handlePreviousStep = React.useCallback(() => {
        if (isEdit) {
            onPrevious(isValid, values);
        }
        clickPrevious();
    }, [clickPrevious, isEdit, isValid, onPrevious, values]);

    const isIbs = React.useMemo(() => posProvider === EPointOfSaleProvider.IBS, [posProvider]);

    const isSimphony = React.useMemo(
        () => posProvider === EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY,
        [posProvider]
    );

    const isBasketQualificationVisible = React.useMemo(() => {
        if (auto && isIbs) {
            return false;
        }
        if (values.discount?.scope === 'BASKET') {
            return true;
        }
        return [
            values.discount?.scope,
            values.discount?.rule,
            values.discount?.groupedScopeMatchCodes?.length > 0,
            values.discount?.value,
            values.discount?.type
        ].every(isDefined);
    }, [
        auto,
        isIbs,
        values.discount?.rule,
        values.discount?.scope,
        values.discount?.groupedScopeMatchCodes,
        values.discount?.type,
        values.discount?.value
    ]);

    const isDateTimeRestrictionVisible = React.useMemo(() => {
        const isBasketQualificationValid = (
            values.basketQualification?.scope === LoyaltyScope.BASKET
                ? [values.basketQualification.scope]
                : [values.basketQualification?.scope, values.basketQualification?.matchCode]
        ).every(isDefined);
        // Skip basket validation if it is disabled
        if (values.basketQualification?.disableBasketQualification) {
            return isBasketQualificationVisible;
        }
        if (isSimphony) {
            return isBasketQualificationValid;
        }
        return auto || isBasketQualificationValid;
    }, [
        auto,
        isSimphony,
        values.basketQualification?.matchCode,
        values.basketQualification?.scope,
        values.basketQualification?.disableBasketQualification,
        isBasketQualificationVisible
    ]);

    const isDiscountVisible = React.useMemo(() => {
        if (isSimphony) {
            return true;
        }
        return !auto;
    }, [auto, isSimphony]);

    const handleSubmit = React.useCallback(() => {
        setTimeout(() => {
            submitForm();
        }, 0);
    }, [submitForm]);

    const discountValueString = React.useMemo(() => {
        if (values.discount?.type === 'OVERRIDE') {
            return `${currencySymbol}${values.discount?.value} override`;
        }
        if (values.discount?.type === 'PERCENTAGE') {
            return `${values.discount?.value}% off`;
        }
        return `${currencySymbol}${values.discount?.value} off`;
    }, [currencySymbol, values.discount?.type, values.discount?.value]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box padding={3} maxWidth={866}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h4">4. Redemption</Typography>
                            <Typography variant="body2">
                                Set up how the award will behave for your users when redeeming through
                                ordering in your app
                            </Typography>
                        </Grid>
                        {isDiscountVisible && (
                            <PerkDiscountSection
                                currencySymbol={currencySymbol}
                                perkType={perkType}
                                posProvider={posProvider}
                                isPseudoCurrency={isPseudoCurrency}
                            />
                        )}
                        {isBasketQualificationVisible && (
                            <PerkBasketQualificationSection
                                currencySymbol={currencySymbol}
                                isPseudoCurrency={isPseudoCurrency}
                                discountValueString={discountValueString}
                                basketScopeOptions={basketScopeOptions}
                                toggledFields={toggledFields}
                                onToggleChange={onToggleChange}
                            />
                        )}
                        {isDateTimeRestrictionVisible && (
                            <PerkDateTimeRestrictionSection
                                auto={auto}
                                isIbs={isIbs}
                                discountValueString={discountValueString}
                                merchantTimezone={merchantTimezone}
                            />
                        )}
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Row flex={1} gutter align="flex-end">
                    {isEdit && (
                        <LoadingButton
                            loading={isLoading}
                            disabled={isLoading || !isValid}
                            onClick={handleUpdate}
                            color="primary"
                            variant="outlined"
                        >
                            Update & Close
                        </LoadingButton>
                    )}
                    <Button onClick={handlePreviousStep} color="primary" variant="outlined">
                        Previous
                    </Button>
                    <Button
                        disabled={!isValid || isLoading}
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Next
                    </Button>
                </Row>
            </Grid>
        </Grid>
    );
};
